import { searchDiscountWithTransaction, searchLocationWithTransaction } from "apis";
import { searchGuestProfile } from "apis/guest-profile.api";
import {
  FilterBar,
  FilterDateRange,
  FilterMultiSelect,
  FilterSingleSelect,
  FilterTimeRange,
} from "components/commons";
import { useApi, useMount } from "hooks";
import {
  locationWithTransactionFilterResponse,
  locationWithTransactionFilterRequest,
  discountWithTransactionFilterRequest,
  discountWithTransactionFilterResponse,
  guestProfileFilterRequest,
  guestProfileFilterResponse,
} from "mappers";
import React, { useCallback, useMemo } from "react";
import { mapObject, mapObjectsToSelect } from "services";
import lang from "translations";

const DiscountReportFilter = ({
  filterState,
  requestState,
  modifyFilter,
  modifyFilters,
  clearFilter,
  onClear,
  onApply,
}) => {
  const {
    request: searchLocationRequest,
    loading: loadingLocation,
    mappedData: locations,
  } = useApi({
    api: searchLocationWithTransaction,
    isArray: true,
    mapper: locationWithTransactionFilterResponse,
  });

  const {
    request: searchDiscountRequest,
    loading: loadingDiscount,
    mappedData: discounts,
  } = useApi({
    api: searchDiscountWithTransaction,
    isArray: true,
    mapper: discountWithTransactionFilterResponse,
  });

  const {
    request: searchGuestProfileRequest,
    loading: loadingGuestProfile,
    mappedData: guestProfiles,
  } = useApi({
    api: searchGuestProfile,
    isArray: true,
    params: {
      includeDeleted: true,
    },
    mapper: guestProfileFilterResponse,
  });

  useMount(() => {
    fetchLocations(requestState);
    fetchDiscounts(requestState);
    fetchGuestProfiles(requestState);
  });

  const fetchLocations = useCallback(
    (requestState) => {
      searchLocationRequest(mapObject({ ...requestState }, locationWithTransactionFilterRequest));
    },
    [searchLocationRequest]
  );

  const fetchDiscounts = useCallback(
    (requestState) => {
      searchDiscountRequest(mapObject({ ...requestState }, discountWithTransactionFilterRequest));
    },
    [searchDiscountRequest]
  );

  const fetchGuestProfiles = useCallback(
    (requestState) => {
      searchGuestProfileRequest(mapObject({ ...requestState }, guestProfileFilterRequest));
    },
    [searchGuestProfileRequest]
  );

  const applyFilterCb = useCallback(
    async (searchKey) => {
      await onApply(searchKey);
    },
    [onApply]
  );

  const clearFilterCb = useCallback(async () => {
    const { filterState, requestState } = await clearFilter();
    onClear(filterState, requestState);
    fetchLocations(requestState);
    fetchDiscounts(requestState);
    fetchGuestProfiles(requestState);
  }, [clearFilter, onClear, fetchLocations, fetchDiscounts, fetchGuestProfiles]);

  const changeDateRangeCb = useCallback(
    (value) => {
      const { requestState } = modifyFilter("dateRange", { value });
      fetchLocations(requestState);
      fetchDiscounts(requestState);
    },
    [fetchLocations, fetchDiscounts, modifyFilter]
  );

  const locationOptions = useMemo(() => {
    return mapObjectsToSelect(locations, { textKey: "name" });
  }, [locations]);

  const guestProfileOptions = useMemo(() => {
    const uniqueGuestProfiles = [
      ...new Map(guestProfiles.map((guestProfile) => [guestProfile["id"], guestProfile])).values(),
    ];
    return mapObjectsToSelect(uniqueGuestProfiles, { valueKey: "id", textKey: "name" });
  }, [guestProfiles]);

  const discountOptions = useMemo(() => {
    let sorted = discounts
      .map((discount) => {
        discount.name = discount.name.trim();
        return discount;
      })
      .sort((a, b) => (a.name > b.name ? 1 : -1));

    return [
      { text: lang.allDiscounts, value: null },
      ...mapObjectsToSelect(sorted, { textKey: "name" }),
    ];
  }, [discounts]);

  return (
    <FilterBar
      className="my-lg"
      filterState={filterState}
      searchBox={false}
      onClear={clearFilterCb}
      onApply={applyFilterCb}
    >
      <FilterDateRange
        id="dateRange"
        label={lang.dateRange}
        maxDays={90}
        value={filterState.dateRange}
        onChange={changeDateRangeCb}
      />
      <FilterTimeRange
        id="timeRange"
        label={lang.timeRange}
        value={filterState.dateRange}
        onChange={(value) => modifyFilter("dateRange", { value })}
      />
      <FilterMultiSelect
        id="locations"
        label={lang.outlet}
        allLabel={lang.allLocations}
        emptyLabel={lang.noLocationAvailable}
        disableWhenEmpty={false}
        loading={loadingLocation}
        options={locationOptions}
        value={filterState.locations}
        onChange={(value) => modifyFilter("locations", { value })}
      />
      <FilterMultiSelect
        id="guestProfiles"
        label={lang.guestProfile}
        allLabel={
          guestProfileOptions.length ? lang.allGuestProfiles : lang.noAvailableGuestProfile
        }
        loading={loadingGuestProfile}
        options={guestProfileOptions}
        value={filterState.guestProfiles}
        onChange={(value) => modifyFilter("guestProfiles", { value })}
      />
      <FilterSingleSelect
        id="discount"
        label={lang.discountName}
        loading={loadingDiscount}
        summaryFallback={lang.allDiscounts}
        options={discountOptions}
        value={filterState.discount}
        onChange={(value) => modifyFilters({ discount: value, guestProfile: null })}
      />
    </FilterBar>
  );
};

export default DiscountReportFilter;
